import { useState, useEffect } from 'react';
import Modal from '../common/Modal';
import { Button } from '../common/Button';
import { Badge } from '../common/Badge';
import toast from 'react-hot-toast';
import type { OrderRecord } from '../../types/orders';

interface Package {
  id?: number;
  length_mm?: number;
  width_mm?: number;
  height_mm?: number;
  weight_kg?: number;
}

interface Carrier {
  id: number;
  name: string;
  email?: string;
}

interface EmailRecipient {
  name?: string;
  email: string;
}

interface CarrierInvitation {
  carrier_id: number;
  carrier_name?: string;
  recipient_emails: {
    main_email?: string;
    forwarder_emails: (string | EmailRecipient)[];
    driver_emails: (string | EmailRecipient)[];
  };
}

interface OrderDetail {
  order_id: number;
  order_number?: string;
  ref_name?: string;
  order_date?: string;
  responsible?: string;
  product_short_description?: string;
  estimated_delivery_date?: string;
  packages?: Package[];
}

interface Transport {
  id: number;
  reference?: string;
  status: string;
  readiness_date?: string;
  delivery_address?: string;
  client_name?: string;
  client_surname?: string;
  client_company?: string;
  client_nip?: string;
  client_email?: string;
  notes?: string;
  created_at?: string;
  packages?: Package[];
  carrier_invitations?: CarrierInvitation[];
  orders?: OrderDetail[];
}

interface TransportDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  transport: Transport | null;
  onOrderClick?: (orderId: number) => void;
}

const STATUS_LABELS: Record<string, string> = {
  draft: 'Przygotowany',
  rfq_sent: 'Zapytania wysłane',
  offers_received: 'Otrzymano oferty',
  offer_selected: 'Wybrano ofertę',
  booked: 'Zarezerwowano',
  delivered: 'Dostarczono',
  closed: 'Zamknięty'
};

const STATUS_COLORS: Record<string, string> = {
  draft: 'bg-gray-100 text-gray-700',
  rfq_sent: 'bg-blue-100 text-blue-700',
  offers_received: 'bg-orange-100 text-orange-700',
  offer_selected: 'bg-green-100 text-green-700',
  booked: 'bg-purple-100 text-purple-700',
  delivered: 'bg-green-200 text-green-800',
  closed: 'bg-gray-200 text-gray-600'
};

export const TransportDetailsModal = ({ isOpen, onClose, transport, onOrderClick }: TransportDetailsModalProps) => {
  const [selectedOrderForDetails, setSelectedOrderForDetails] = useState<OrderRecord | null>(null);
  const [carriers, setCarriers] = useState<Carrier[]>([]);
  const [isEditing, setIsEditing] = useState(false);
  const [editedTransport, setEditedTransport] = useState<Transport | null>(null);

  // Load carriers to display names
  useEffect(() => {
    const loadCarriers = async () => {
      try {
        const resp = await fetch('/api/v1/transport/carriers/list', {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('access_token')}`
          }
        });
        const data = await resp.json();
        console.log('Carriers loaded:', data);
        setCarriers(Array.isArray(data) ? data : []);
      } catch (e) {
        console.error('Failed to load carriers', e);
        setCarriers([]);
      }
    };
    if (isOpen) {
      loadCarriers();
    }
  }, [isOpen]);

  // Initialize edited transport when modal opens or transport changes
  useEffect(() => {
    if (transport) {
      setEditedTransport(JSON.parse(JSON.stringify(transport))); // Deep clone
      setIsEditing(true); // Otwieraj od razu w trybie edycji
    }
  }, [transport]);

  if (!transport || !editedTransport) {
    return null;
  }

  const displayTransport = isEditing ? editedTransport : transport;

  const handleSaveChanges = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await fetch(`/api/v1/transport/${transport.id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          readiness_date: editedTransport.readiness_date,
          client_name: editedTransport.client_name,
          client_surname: editedTransport.client_surname,
          client_company: editedTransport.client_company,
          client_nip: editedTransport.client_nip,
          client_email: editedTransport.client_email,
          delivery_address: editedTransport.delivery_address,
          packages: editedTransport.packages,
          notes: editedTransport.notes
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Nie udało się zapisać zmian');
      }

      toast.success('Zmiany zostały zapisane');
      setIsEditing(false);
      onClose(); // Close and let parent refresh
    } catch (error: any) {
      console.error('Error saving transport:', error);
      toast.error(error.message || 'Wystąpił błąd podczas zapisywania zmian');
    }
  };

  const handleCancelEdit = () => {
    // Zamknij modal bez zapisywania zmian
    onClose();
  };

  const addPackage = () => {
    const newPackages = [...(editedTransport.packages || []), { length_mm: undefined, width_mm: undefined, height_mm: undefined, weight_kg: undefined }];
    setEditedTransport({ ...editedTransport, packages: newPackages });
  };

  const removePackage = (index: number) => {
    const newPackages = editedTransport.packages?.filter((_, i) => i !== index) || [];
    setEditedTransport({ ...editedTransport, packages: newPackages });
  };

  const updatePackage = (index: number, field: keyof Package, value: number) => {
    const newPackages = [...(editedTransport.packages || [])];
    newPackages[index] = { ...newPackages[index], [field]: value };
    setEditedTransport({ ...editedTransport, packages: newPackages });
  };

  const handleOrderClick = (order: OrderDetail) => {
    // Convert OrderDetail to OrderRecord for the modal
    setSelectedOrderForDetails({
      id: order.order_id,
      order_nr: order.order_number || '',
      ref_name: order.ref_name,
      order_date: order.order_date,
      responsible: order.responsible,
      product_short_description: order.product_short_description,
      estimated_delivery_date: order.estimated_delivery_date,
    } as OrderRecord);
  };

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} title={`Szczegóły zlecenia transportowego #${transport.id}`} size="xl">
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Status */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <div>
                <Badge className={STATUS_COLORS[displayTransport.status]}>
                  {STATUS_LABELS[displayTransport.status] || displayTransport.status}
                </Badge>
              </div>
            </div>

            {/* Data utworzenia */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Data utworzenia</label>
              <div className="text-sm text-gray-900">
                {displayTransport.created_at ? new Date(displayTransport.created_at).toLocaleDateString('pl-PL') : '-'}
              </div>
            </div>

            {/* Data gotowości - editable */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Data gotowości do transportu</label>
              {isEditing ? (
                <input
                  type="date"
                  value={editedTransport.readiness_date || ''}
                  onChange={(e) => setEditedTransport({ ...editedTransport, readiness_date: e.target.value })}
                  className="w-full border rounded px-3 py-2"
                />
              ) : (
                <div className="text-sm font-semibold text-blue-700">{displayTransport.readiness_date || '-'}</div>
              )}
            </div>

            {/* Separator - Dane klienta */}
            <div className="md:col-span-2 border-t pt-4">
              <label className="block text-sm font-semibold text-gray-800 mb-3">Dane klienta</label>
            </div>

            {/* Imię */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Imię</label>
              {isEditing ? (
                <input
                  type="text"
                  value={editedTransport.client_name || ''}
                  onChange={(e) => setEditedTransport({ ...editedTransport, client_name: e.target.value })}
                  className="w-full border rounded px-3 py-2"
                  placeholder="Imię"
                />
              ) : (
                <div className="text-sm text-gray-900">{displayTransport.client_name || '-'}</div>
              )}
            </div>

            {/* Nazwisko */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Nazwisko</label>
              {isEditing ? (
                <input
                  type="text"
                  value={editedTransport.client_surname || ''}
                  onChange={(e) => setEditedTransport({ ...editedTransport, client_surname: e.target.value })}
                  className="w-full border rounded px-3 py-2"
                  placeholder="Nazwisko"
                />
              ) : (
                <div className="text-sm text-gray-900">{displayTransport.client_surname || '-'}</div>
              )}
            </div>

            {/* Firma */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Firma</label>
              {isEditing ? (
                <input
                  type="text"
                  value={editedTransport.client_company || ''}
                  onChange={(e) => setEditedTransport({ ...editedTransport, client_company: e.target.value })}
                  className="w-full border rounded px-3 py-2"
                  placeholder="Nazwa firmy"
                />
              ) : (
                <div className="text-sm text-gray-900">{displayTransport.client_company || '-'}</div>
              )}
            </div>

            {/* NIP */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">NIP</label>
              {isEditing ? (
                <input
                  type="text"
                  value={editedTransport.client_nip || ''}
                  onChange={(e) => setEditedTransport({ ...editedTransport, client_nip: e.target.value })}
                  className="w-full border rounded px-3 py-2"
                  placeholder="NIP"
                />
              ) : (
                <div className="text-sm text-gray-900">{displayTransport.client_nip || '-'}</div>
              )}
            </div>

            {/* E-mail */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">E-mail</label>
              {isEditing ? (
                <input
                  type="email"
                  value={editedTransport.client_email || ''}
                  onChange={(e) => setEditedTransport({ ...editedTransport, client_email: e.target.value })}
                  className="w-full border rounded px-3 py-2"
                  placeholder="email@example.com"
                />
              ) : (
                <div className="text-sm text-gray-900">{displayTransport.client_email || '-'}</div>
              )}
            </div>

            {/* Adres */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Adres dostawy</label>
              {isEditing ? (
                <textarea
                  value={editedTransport.delivery_address || ''}
                  onChange={(e) => setEditedTransport({ ...editedTransport, delivery_address: e.target.value })}
                  className="w-full border rounded px-3 py-2"
                  rows={2}
                  placeholder="Ulica, numer, kod pocztowy, miasto"
                />
              ) : (
                <div className="text-sm text-gray-900">{displayTransport.delivery_address || '-'}</div>
              )}
            </div>
          </div>

          {/* Zamówienia jako kafelki */}
          <div className="border-t pt-4">
            <label className="block text-sm font-semibold text-gray-800 mb-3">
              Zamówienia ({transport.orders?.length || 0})
            </label>
            {!transport.orders || transport.orders.length === 0 ? (
              <div className="text-center py-8 text-gray-500 bg-gray-50 rounded-lg">
                Brak zamówień przypisanych do tego zlecenia
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {transport.orders.map((order, idx) => (
                  <div
                    key={order.order_id || idx}
                    onClick={() => handleOrderClick(order)}
                    className="border rounded-lg p-3 bg-gradient-to-br from-blue-50 to-blue-100 border-blue-300 cursor-pointer hover:shadow-md transition-shadow"
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <div className="font-semibold text-blue-900">{order.order_number || 'Brak numeru'}</div>
                        <div className="text-xs text-blue-700">{order.ref_name || 'Brak nazwy'}</div>
                      </div>
                      <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </div>
                    <div className="text-xs text-blue-600">
                      {order.responsible && <div>Odpowiedzialny: {order.responsible}</div>}
                      {order.order_date && <div>Data: {order.order_date}</div>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Przewoźnicy */}
          <div className="border-t pt-4">
            <label className="block text-sm font-semibold text-gray-800 mb-3">
              Wybrani przewoźnicy ({transport.carrier_invitations?.length || 0})
            </label>
            {!transport.carrier_invitations || transport.carrier_invitations.length === 0 ? (
              <div className="text-center py-6 text-gray-500 bg-gray-50 rounded-lg">
                Brak wybranych przewoźników
              </div>
            ) : (
              <div className="space-y-3">
                {transport.carrier_invitations.map((inv, idx) => {
                  const carrierName = inv.carrier_name || carriers.find(c => c.id === inv.carrier_id)?.name || `Przewoźnik #${inv.carrier_id}`;

                  // Collect all recipients with their roles
                  const recipients = [];
                  if (inv.recipient_emails.main_email) {
                    recipients.push({ type: 'Email główny', email: inv.recipient_emails.main_email });
                  }
                  inv.recipient_emails.forwarder_emails.forEach((item: any) => {
                    recipients.push({
                      type: 'Spedytor',
                      name: typeof item === 'string' ? undefined : item.name,
                      email: typeof item === 'string' ? item : item.email
                    });
                  });
                  inv.recipient_emails.driver_emails.forEach((item: any) => {
                    recipients.push({
                      type: 'Kierowca',
                      name: typeof item === 'string' ? undefined : item.name,
                      email: typeof item === 'string' ? item : item.email
                    });
                  });

                  return (
                    <div key={idx} className="p-4 bg-gray-50 rounded-lg border">
                      <div className="flex items-center justify-between mb-3">
                        <div className="font-semibold text-lg text-gray-800">{carrierName}</div>
                        <Badge variant="secondary" className="text-xs">
                          {recipients.length} email{recipients.length !== 1 ? 'i' : ''}
                        </Badge>
                      </div>
                      <div className="space-y-2">
                        {recipients.map((recipient, recipientIdx) => (
                          <div key={recipientIdx} className="flex items-start gap-2 text-sm">
                            <Badge variant="outline" className="text-xs shrink-0">
                              {recipient.type}
                            </Badge>
                            <div className="flex-1">
                              {recipient.name && (
                                <div className="font-medium text-gray-900">{recipient.name}</div>
                              )}
                              <div className="text-gray-600 break-all">{recipient.email}</div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Paczki */}
          <div className="border-t pt-4">
            <div className="flex items-center justify-between mb-3">
              <label className="block text-sm font-semibold text-gray-800">
                Wymiar ładowny ({displayTransport.packages?.length || 0} paczek)
              </label>
              {isEditing && (
                <Button onClick={addPackage} size="sm" variant="outline">
                  + Dodaj paczkę
                </Button>
              )}
            </div>
            {!displayTransport.packages || displayTransport.packages.length === 0 ? (
              <div className="text-center py-6 text-gray-500 bg-gray-50 rounded-lg">
                Brak informacji o paczkach
              </div>
            ) : (
              <div className="space-y-2">
                {displayTransport.packages.map((pkg, idx) => (
                  <div key={idx} className="flex items-center gap-4 p-3 bg-gray-50 rounded-lg border">
                    {isEditing ? (
                      <>
                        <div className="flex-1 grid grid-cols-4 gap-2">
                          <div>
                            <div className="text-xs text-gray-500 mb-1">Długość (mm)</div>
                            <input
                              type="number"
                              value={pkg.length_mm ?? ''}
                              onChange={(e) => updatePackage(idx, 'length_mm', Number(e.target.value))}
                              className="w-full border rounded px-2 py-1 text-sm"
                              placeholder="0"
                            />
                          </div>
                          <div>
                            <div className="text-xs text-gray-500 mb-1">Szerokość (mm)</div>
                            <input
                              type="number"
                              value={pkg.width_mm ?? ''}
                              onChange={(e) => updatePackage(idx, 'width_mm', Number(e.target.value))}
                              className="w-full border rounded px-2 py-1 text-sm"
                              placeholder="0"
                            />
                          </div>
                          <div>
                            <div className="text-xs text-gray-500 mb-1">Wysokość (mm)</div>
                            <input
                              type="number"
                              value={pkg.height_mm ?? ''}
                              onChange={(e) => updatePackage(idx, 'height_mm', Number(e.target.value))}
                              className="w-full border rounded px-2 py-1 text-sm"
                              placeholder="0"
                            />
                          </div>
                          <div>
                            <div className="text-xs text-gray-500 mb-1">Waga (kg)</div>
                            <input
                              type="number"
                              value={pkg.weight_kg ?? ''}
                              onChange={(e) => updatePackage(idx, 'weight_kg', Number(e.target.value))}
                              className="w-full border rounded px-2 py-1 text-sm"
                              step="0.1"
                              placeholder="0"
                            />
                          </div>
                        </div>
                        <button
                          onClick={() => removePackage(idx)}
                          className="p-2 text-red-600 hover:bg-red-50 rounded transition-colors"
                          title="Usuń paczkę"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </>
                    ) : (
                      <div className="flex-1 grid grid-cols-4 gap-2 text-sm">
                        <div>
                          <div className="text-xs text-gray-500">Długość</div>
                          <div className="font-medium">{pkg.length_mm || '-'} mm</div>
                        </div>
                        <div>
                          <div className="text-xs text-gray-500">Szerokość</div>
                          <div className="font-medium">{pkg.width_mm || '-'} mm</div>
                        </div>
                        <div>
                          <div className="text-xs text-gray-500">Wysokość</div>
                          <div className="font-medium">{pkg.height_mm || '-'} mm</div>
                        </div>
                        <div>
                          <div className="text-xs text-gray-500">Waga</div>
                          <div className="font-medium">{pkg.weight_kg || '-'} kg</div>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Notatki */}
          <div className="border-t pt-4">
            <label className="block text-sm font-semibold text-gray-800 mb-2">Notatki</label>
            {isEditing ? (
              <textarea
                value={editedTransport.notes || ''}
                onChange={(e) => setEditedTransport({ ...editedTransport, notes: e.target.value })}
                className="w-full border rounded px-3 py-2"
                rows={3}
                placeholder="Dodaj notatki..."
              />
            ) : (
              <div className="bg-yellow-50 border border-yellow-200 rounded p-3 text-sm">
                {displayTransport.notes || 'Brak notatek'}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex justify-end items-center gap-2 mt-4">
            <Button onClick={handleCancelEdit} variant="outline">
              Anuluj
            </Button>
            <Button onClick={handleSaveChanges} className="bg-green-600 hover:bg-green-700 text-white">
              Zapisz
            </Button>
          </div>
        </div>
      </Modal>

      {/* Modal szczegółów pojedynczego zamówienia */}
      {selectedOrderForDetails && (
        <Modal
          isOpen={!!selectedOrderForDetails}
          onClose={() => setSelectedOrderForDetails(null)}
          title={`Szczegóły zamówienia ${selectedOrderForDetails.order_nr}`}
          size="lg"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Numer zamówienia</label>
              <div className="text-sm text-gray-900">{selectedOrderForDetails.order_nr || '-'}</div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Nazwa ref.</label>
              <div className="text-sm text-gray-900">{selectedOrderForDetails.ref_name || '-'}</div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Odpowiedzialny</label>
              <div className="text-sm text-gray-900">{selectedOrderForDetails.responsible || '-'}</div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Data zamówienia</label>
              <div className="text-sm text-gray-900">{selectedOrderForDetails.order_date || '-'}</div>
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">Adres dostawy</label>
              <div className="text-sm text-gray-900">{selectedOrderForDetails.delivery_address || '-'}</div>
            </div>
            {selectedOrderForDetails.estimated_delivery_date && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Szacowana dostawa</label>
                <div className="text-sm text-gray-900">{selectedOrderForDetails.estimated_delivery_date}</div>
              </div>
            )}
            {selectedOrderForDetails.confirmed_collection_date && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Potwierdzony termin załadunku</label>
                <div className="text-sm text-gray-900">{selectedOrderForDetails.confirmed_collection_date}</div>
              </div>
            )}
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={() => setSelectedOrderForDetails(null)}>
              Zamknij
            </Button>
          </div>
        </Modal>
      )}
    </>
  );
};
